import XE from "@components/common/xe";
import { Button, Text } from "@uikit";
import dayjs from "dayjs";
import * as React from "react";

interface iRateItem {
  rate: any;
  number_of_golfers: number;
  display_currency: any;
  onSelected: (rate: any) => void;
}

export const RateItem = ({
  rate,
  number_of_golfers,
  display_currency,
  onSelected,
}: iRateItem) => {
  const [loading, setLoading] = React.useState(false);
  const rate_info = (rate: any) => {
    let surcharge =
      rate.odd_number_surcharge > 0
        ? number_of_golfers % 2 && rate.odd_number_surcharge / number_of_golfers
        : 0;

    return (
      <span className="inline-flex items-end gap-[2px]">
        <XE
          base_currency={rate.base_currency}
          currency={display_currency.code}
          value={
            Math.round((Number(rate.sell_price) + Number(surcharge)) * 100) /
            100
          }
        />
        <Text size={11} leading={1.3} color="black80" className="!mb-[2px]">
          pp
        </Text>
      </span>
    );
  };

  const rate_name_info = (rate: any) => {
    if (rate.product_type_name.en === "Standard")
      return `${rate.product_name.en}`;

    return `${rate.product_name.en} (${rate.product_type_name.en})`;
  };

  const hole_options = (rate: any) => {
    if (!rate?.hole_options) return null;
    if (rate?.hole_options?.length === 0) return null;

    let option = rate.hole_options?.find(
      (hole_option: any) => hole_option.id === rate.course_hole_combination
    );

    if (!option) return null;
    return <>{option.description} · </>;
  };

  const product_only_single_caddie = (rate: any) => {
    return (
      rate.product_config.single_cart === false &&
      rate.product_config.shared_cart === false &&
      rate.product_config.shared_caddie === false &&
      rate.product_config.caddie === true
    );
  };

  const product_only_shared_caddie = (rate: any) => {
    return (
      rate.product_config.single_cart === false &&
      rate.product_config.shared_cart === false &&
      rate.product_config.shared_caddie === true &&
      rate.product_config.caddie === false
    );
  };

  const product_only_single_cart = (rate: any) => {
    return (
      rate.product_config.single_cart === true &&
      rate.product_config.shared_cart === false &&
      rate.product_config.shared_caddie === false &&
      rate.product_config.caddie === false
    );
  };
  const product_only_shared_cart = (rate: any) => {
    return (
      rate.product_config.single_cart === false &&
      rate.product_config.shared_cart === true &&
      rate.product_config.shared_caddie === false &&
      rate.product_config.caddie === false
    );
  };

  const product_caddie_single_cart = (rate: any) => {
    return (
      rate.product_config.single_cart === true &&
      rate.product_config.shared_cart === false &&
      rate.product_config.shared_caddie === false &&
      rate.product_config.caddie === true
    );
  };

  const product_caddie_shared_cart = (rate: any) => {
    return (
      rate.product_config.single_cart === false &&
      rate.product_config.shared_cart === true &&
      rate.product_config.shared_caddie === false &&
      rate.product_config.caddie === true
    );
  };

  const product_shared_caddie_single_cart = (rate: any) => {
    return (
      rate.product_config.single_cart === true &&
      rate.product_config.shared_cart === false &&
      rate.product_config.shared_caddie === true &&
      rate.product_config.caddie === false
    );
  };

  const product_shared_caddie_shared_cart = (rate: any) => {
    return (
      rate.product_config.single_cart === false &&
      rate.product_config.shared_cart === true &&
      rate.product_config.shared_caddie === true &&
      rate.product_config.caddie === false
    );
  };

  const product_no_caddie = (rate: any) => {
    return (
      rate.product_config.shared_caddie === false &&
      rate.product_config.caddie === false
    );
  };

  const product_no_cart = (rate: any) => {
    return (
      rate.product_config.single_cart === false &&
      rate.product_config.shared_cart === false
    );
  };

  const cart_prepayment_allowed = (rate: any) => {
    return rate.course_policies?.carts?.prepayment_available || false;
  };
  const caddie_prepayment_allowed = (rate: any) => {
    return rate.course_policies?.caddies?.prepayment_available || false;
  };

  const inclusions = (rate: any) => {
    if (product_only_single_caddie(rate)) {
      return <>Includes caddie</>;
    }
    if (product_only_shared_caddie(rate)) {
      return <>Includes shared caddie</>;
    }
    if (product_only_single_cart(rate)) {
      return <>Includes single cart</>;
    }
    if (product_only_shared_cart(rate)) {
      return <>Includes shared cart</>;
    }
    if (product_caddie_single_cart(rate)) {
      return <>Includes caddie and single cart</>;
    }
    if (product_caddie_shared_cart(rate)) {
      return <>Includes caddie and shared cart</>;
    }
    if (product_shared_caddie_single_cart(rate)) {
      return <>Includes shared caddie and single cart</>;
    }
    if (product_shared_caddie_shared_cart(rate)) {
      return <>Includes shared caddie and shared cart</>;
    }
    return null;
  };

  const exclusions = (rate: any) => {
    let excludes_caddie = () => (
      <li>
        <Text size={15} color="black80" leading={1.5}>
          Excludes compulsory caddie (
          <XE
            base_currency={rate.base_currency}
            currency={display_currency.code}
            value={rate.caddie_price}
          />
          {!caddie_prepayment_allowed(rate) && " - pay at course"})
        </Text>
      </li>
    );

    let excludes_cart = () => (
      <li>
        <Text size={15} color="black80" leading={1.5}>
          Excludes compulsory cart (
          {rate.single_cart_enabled && rate.shared_cart_enabled ? "from " : ""}
          <XE
            base_currency={rate.base_currency}
            currency={display_currency.code}
            value={rate.single_cart_price}
          />
          {!cart_prepayment_allowed(rate) && " - pay at course"})
        </Text>
      </li>
    );

    if (
      product_no_caddie(rate) &&
      rate.caddie_policy === "compulsory" &&
      product_no_cart(rate) &&
      rate.cart_policy === "compulsory"
    ) {
      return (
        <>
          {excludes_caddie()}
          {excludes_cart()}
        </>
      );
    }

    if (product_no_caddie(rate) && rate.caddie_policy === "compulsory") {
      return <>{excludes_caddie()}</>;
    }
    if (product_no_cart(rate) && rate.cart_policy === "compulsory") {
      return <>{excludes_cart()}</>;
    }
  };

  const savingsPill = (rate: any) => {
    let surcharge =
      rate.odd_number_surcharge > 0
        ? number_of_golfers % 2 && rate.odd_number_surcharge / number_of_golfers
        : 0;

    let sell_price = Number(rate.sell_price) + Number(surcharge);

    if (sell_price < Number(rate.reference_rate)) {
      let savings_percent = Math.round(
        ((Number(rate.reference_rate) - sell_price) /
          Number(rate.reference_rate)) *
          100
      );
      return (
        <span className="bg-red rounded-sm text-white text-center font-semibold px-1 py-[1px]  text-10 leading-[133%] self-center">
          SAVE {savings_percent}%
        </span>
      );
    }
  };

  const ratePill = (rate: any) => {
    if (rate.type === "PROMOTIONAL_RATE")
      return (
        <div className="bg-red text-white text-center font-semibold px-1  text-10 self-center mb-1">
          PROMO
        </div>
      );
  };

  const getGroupSizeText = (rate: any) => {
    if (rate.min_pax === rate.max_pax) {
      return `${rate.min_pax} players/group`;
    }
    if (rate.multiples === 1) {
      return `${rate.min_pax} to ${rate.max_pax} players/group`;
    }
    if (rate.multiples === 2 && rate.min_pax === 2 && rate.max_pax === 4) {
      return `${rate.min_pax} or ${rate.max_pax} players/group`;
    }

    return `Up to ${rate.max_pax} players/group`;
  };

  return (
    <div className="flex flex-col mb-4 flex-1 border rounded-lg overflow-hidden border-grey3">
      <div
        className="flex flex-col flex-1 px-3 pt-4 pb-5 justify-between gap-3 cursor-pointer hover:bg-grey3/10"
        onClick={() => {
          onSelected(rate);
        }}
      >
        <div>
          <Text size={19} leading={1.3} weight="semibold" className="mb-0">
            {rate_name_info(rate)}
          </Text>

          <Text size={13} color="black60" leading={1.5}>
            {hole_options(rate)} {getGroupSizeText(rate)}
          </Text>
        </div>
        <div className="flex flex-col gap-2">
          <ul className="list-disc ml-5 text-black/70">
            <li>
              <Text size={15} color="black80" leading={1.5}>
                {rate.earliest_time === rate.latest_time ? (
                  <>
                    Tee off at{" "}
                    <strong>
                      {dayjs(rate.earliest_time, "hh:mm:ss")
                        .format("h:mma")
                        .toLowerCase()}
                    </strong>
                  </>
                ) : (
                  <>
                    Tee off between{" "}
                    <strong>
                      {dayjs(rate.earliest_time, "hh:mm:ss")
                        .format("h:mma")
                        .toLowerCase()}
                    </strong>{" "}
                    and{" "}
                    <strong>
                      {dayjs(rate.latest_time.toString(), "hh:mm:ss")
                        .format("h:mma")
                        .toLowerCase()}{" "}
                    </strong>
                  </>
                )}
              </Text>
            </li>
            {rate.note && (
              <li>
                <Text size={15} color="black80" leading={1.5}>
                  {`${rate.note}`}
                </Text>
              </li>
            )}
            {inclusions(rate) && (
              <li>
                <Text size={15} color="black80" leading={1.5}>
                  {inclusions(rate)}
                </Text>
              </li>
            )}
            {exclusions(rate) && <>{exclusions(rate)}</>}
          </ul>
        </div>
      </div>
      <div className="flex  border-t border-grey3 py-2 gap-3 justify-center items-center bg-grey border-l border-grey3/30  px-3">
        <div className="flex flex-1 gap-[2px] flex-col">
          {ratePill(rate)}
          <Text size={17} weight="semibold" color="black" leading={1.3}>
            <div className="flex gap-2 items-end">
              {rate_info(rate)}
              {savingsPill(rate)}
            </div>
          </Text>
          <Text size={10} color="black40" leading={1.3}>
            {`Additional fees may apply${
              display_currency.code !== rate.base_currency
                ? `. Billed in ${rate.base_currency}.`
                : ""
            }`}
          </Text>
        </div>
        <div>
          <Button
            onClick={() => {
              setLoading(true);
              onSelected(rate);
            }}
            loading={loading}
            size="small"
            color="green"
            width="full"
            className={`!my-0 !px-2`}
            text="Book Now"
          />
        </div>
      </div>
    </div>
  );
};
